import { useMemo } from 'react';

import { useMutation, useQuery, useQueryClient } from 'react-query';
import { LowerRole } from '@shared/types';

import {
  createGiftCsMemo,
  deleteGiftCsMemo,
  getGiftCsMemoLog,
  getGiftCsMemoLogAll,
  getGiftCsMemoLogSummary,
  getGiftCsMemos,
  getGiftCsMemosBatch,
  GiftCsMemoRole,
  isCsMemoNotFound,
} from './giftCsMemo.controller';
import {
  GiftCsMemo,
  GiftCsMemoLogCounts,
  GiftCsMemoLogItem,
  GiftCsMemoLogParams,
  GiftCsMemoLogResponse,
  GiftCsMemoLogSummary,
  GiftCsMemoTarget,
  resolveCsMemoTarget,
} from './giftCsMemo.interface';

/**
 * 메모 조회는 전부 이 전용 엔드포인트로만 한다 (virtual populate 미사용).
 *  - 구서버엔 없는 경로라 populate로 끼워 넣으면 StrictPopulateError 500 → 주문목록 전체가 죽는다.
 *    전용 GET은 404로 끝나므로 미배포 서버에서도 화면은 멀쩡하다.
 *  - virtual에는 adminOnly 메모가 아예 안 실린다. 이 GET만 역할 검증 후 포함해준다.
 */
export const giftCsMemoQueryKey = (
  target: GiftCsMemoTarget,
  role: GiftCsMemoRole = LowerRole.Admin,
): string[] => {
  const resolved = resolveCsMemoTarget(target);
  // 역할이 키에 들어가야 admin 캐시와 배송관리자 캐시가 섞이지 않는다
  // (배송관리자 응답은 adminOnly 메모가 빠진 축소본이다)
  return ['gift-cs-memo', role, resolved?.axis ?? 'none', resolved?.id ?? ''];
};

const giftCsMemoBulkQueryKey = (orderIds: string[]): string[] => [
  'gift-cs-memo',
  'orders',
  [...orderIds].sort().join(','),
];

/**
 * 목록 화면에서 한 번에 채울 주문 수 상한 (초과분은 개수 미상 — 배지를 눌러야 조회).
 * 배치 라우트가 100개씩 끊어 받으므로 요청 3건까지 허용한다.
 */
export const CS_MEMO_BULK_LIMIT = 300;

/** 배치 라우트 상한 — 초과하면 서버가 400 (스펙 §3.3) */
const CS_MEMO_BATCH_LIMIT = 100;

/**
 * 폴백(배치 라우트 없는 서버)은 주문 1건당 1요청이라 훨씬 낮게 묶는다.
 * 정상 배포 서버에서는 타지 않는 경로.
 */
const CS_MEMO_FALLBACK_LIMIT = 60;

const CONCURRENCY = 6;

const fetchOrNull = async (
  target: GiftCsMemoTarget,
  role: GiftCsMemoRole = LowerRole.Admin,
): Promise<GiftCsMemo[] | null> => {
  try {
    return await getGiftCsMemos(target, role);
  } catch (e) {
    if (isCsMemoNotFound(e)) return null; // 엔드포인트 미배포 (구서버 안전장치)
    throw e;
  }
};

/**
 * 대상별 메모 타임라인.
 * - `null` = 엔드포인트 미배포(404). `[]` = 메모 없음.
 * - 주문 축으로 조회하면 그 주문의 쿠폰·배송 단위 메모까지 전부 실려온다 → 상세 모달은
 *   주문 축 한 번만 조회하고 화면에서 갈라 쓴다.
 */
export const useGiftCsMemoListQuery = (
  target: GiftCsMemoTarget,
  enabled = true,
  role: GiftCsMemoRole = LowerRole.Admin,
) => {
  const resolved = resolveCsMemoTarget(target);
  return useQuery<GiftCsMemo[] | null>(
    giftCsMemoQueryKey(target, role),
    () => fetchOrNull(target, role),
    // retry 1 — 메모는 부가 정보라 서버가 흔들릴 때 요청을 불리지 않는다(기본값 3회)
    { enabled: enabled && !!resolved, staleTime: 30 * 1000, retry: 1 },
  );
};

/**
 * 조회는 하지 않고 캐시만 구독한다 (배지 개수 동기화용).
 * 메모장을 한 번 열었던 대상은 저장 즉시 개수가 갱신된다.
 */
export const useGiftCsMemoCache = (
  target: GiftCsMemoTarget,
  role: GiftCsMemoRole = LowerRole.Admin,
): GiftCsMemo[] | null | undefined =>
  useQuery<GiftCsMemo[] | null>(
    giftCsMemoQueryKey(target, role),
    () => fetchOrNull(target, role),
    { enabled: false },
  ).data;

export interface GiftCsMemosByOrderResult {
  /** orderId → 그 주문의 전체 메모 (쿠폰·배송 단위 포함) */
  memoMap: Map<string, GiftCsMemo[]>;
  isLoading: boolean;
  /** true = 백엔드 미배포(404) → 메모 UI 자체를 숨기는 용도 */
  unavailable: boolean;
}

/** 주문별 개별 조회 폴백 — 배치 라우트만 없는 서버용 (동시 6건, 60주문까지) */
const fetchByOrderFallback = async (
  orderIds: string[],
): Promise<Record<string, GiftCsMemo[]> | null> => {
  const result: Record<string, GiftCsMemo[]> = {};
  const ids = orderIds.slice(0, CS_MEMO_FALLBACK_LIMIT);

  for (let i = 0; i < ids.length; i += CONCURRENCY) {
    const chunk = ids.slice(i, i + CONCURRENCY);
    const settled = await Promise.all(
      chunk.map(async (orderId) => ({
        orderId,
        memos: await fetchOrNull({ giftOrder: orderId }),
      })),
    );
    for (const { orderId, memos } of settled) {
      if (memos === null) return null; // 기능 자체가 미배포
      result[orderId] = memos;
    }
  }

  return result;
};

/**
 * 검색 결과 목록용 — 여러 주문의 메모를 배치(§3.3)로 한 번에 채운다.
 * 배치 라우트가 없는 서버면 주문별 개별 조회로 폴백하고, 그것도 404면 unavailable.
 * 개별 주문 캐시에도 심어두므로 메모장을 열면 재조회 없이 바로 뜬다.
 */
export const useGiftCsMemosByOrders = (
  orderIds: string[],
  enabled = true,
): GiftCsMemosByOrderResult => {
  const queryClient = useQueryClient();
  const ids = orderIds.filter(Boolean);
  const capped = ids.slice(0, CS_MEMO_BULK_LIMIT);

  const { data, isLoading } = useQuery<Record<string, GiftCsMemo[]> | null>(
    giftCsMemoBulkQueryKey(capped),
    async () => {
      let result: Record<string, GiftCsMemo[]> | null = null;
      try {
        const merged: Record<string, GiftCsMemo[]> = {};
        for (let i = 0; i < capped.length; i += CS_MEMO_BATCH_LIMIT) {
          const chunk = capped.slice(i, i + CS_MEMO_BATCH_LIMIT);
          Object.assign(merged, await getGiftCsMemosBatch(chunk));
        }
        result = merged;
      } catch (e) {
        if (!isCsMemoNotFound(e)) throw e;
        result = await fetchByOrderFallback(capped);
      }

      // 메모장(패널)이 그대로 재사용하도록 개별 캐시에도 심는다.
      // 배치 응답은 메모 없는 주문도 빈 배열로 오므로 "0건"이 그대로 캐시된다.
      if (result) {
        for (const [orderId, memos] of Object.entries(result)) {
          queryClient.setQueryData(giftCsMemoQueryKey({ giftOrder: orderId }), memos);
        }
      }

      return result;
    },
    // retry 1 — 폴백 경로는 주문 수만큼 요청이라 재시도를 곱하면 안 된다
    { enabled: enabled && capped.length > 0, staleTime: 30 * 1000, retry: 1 },
  );

  const memoMap = useMemo(() => {
    const map = new Map<string, GiftCsMemo[]>();
    if (data) {
      for (const [orderId, memos] of Object.entries(data)) map.set(orderId, memos);
    }
    return map;
  }, [data]);

  return { memoMap, isLoading, unavailable: data === null };
};

/**
 * CS 응대 로그 목록 (기간·단위·검색). `null` = 엔드포인트 미배포(404).
 * 메모를 남기면 목록도 갱신되도록 create/delete가 이 키를 무효화한다.
 */
export const useGiftCsMemoLogQuery = (params: GiftCsMemoLogParams, enabled = true) =>
  useQuery<GiftCsMemoLogResponse | null>(
    ['gift-cs-memo', 'log', JSON.stringify(params)],
    async () => {
      try {
        return await getGiftCsMemoLog(params);
      } catch (e) {
        if (isCsMemoNotFound(e)) return null;
        throw e;
      }
    },
    // 창 포커스 리페치를 끈다 — 이 화면의 조회 한 번이 서버에선 10여 쿼리(대상 요약 조인)라
    // 탭만 왔다갔다 해도 부하가 그대로 곱해진다. 최신화는 메모 작성·삭제 시 무효화로 충분하다.
    {
      enabled,
      keepPreviousData: true,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

/**
 * 목록 위에 뜨는 숫자 전부 — 축별 건수(필터 칩)와 분포 칩(작성자·유형·상태·상조).
 *
 * 서버 요약 엔드포인트가 있으면 **1요청**으로 끝난다. 없으면(구서버) 축별 `total`만
 * 3요청으로 받아 칩 숫자는 살리고, 분포는 `null` → 화면이 "이 페이지" 기준으로 근사한다.
 *
 * ⚠️ 예전에는 분포를 그리려고 목록 전체(200건 × 최대 10요청)를 끌어와 프론트에서 셌다.
 *    칩 네 줄 값으로는 너무 비싸서(요청당 서버 쿼리 10여 개) 서버 집계로 옮겼다.
 */
export interface GiftCsMemoLogOverview {
  counts: GiftCsMemoLogCounts;
  /** 조회 조건 **전체** 기준 분포 — 서버 미배포면 null */
  distributions: Omit<GiftCsMemoLogSummary, 'counts'> | null;
}

/**
 * 목록과 같은 조건에서 페이징만 뺀 것.
 *
 * `target`은 **분포에만** 걸리는 조건이다 — 축 칩 숫자(`counts`)는 축을 고르기 **전에**
 * 보는 값이라 언제나 전체 기준이어야 하고, 분포 칩은 지금 보고 있는 축의 것이어야 한다.
 * 서버 요약도 같은 규칙으로 계약했다(스펙 §3).
 * (구서버 폴백에서는 축을 바꿀 때 축별 total 3요청이 다시 나가지만, 전부 `limit=1`
 *  카운트라 예전처럼 목록 전량을 다시 끌어오는 것과는 비용이 다르다)
 */
const overviewBaseParams = (params: GiftCsMemoLogParams): GiftCsMemoLogParams => ({
  ...(params.from ? { from: params.from } : {}),
  ...(params.to ? { to: params.to } : {}),
  ...(params.target && params.target !== 'all' ? { target: params.target } : {}),
  ...(params.q ? { q: params.q } : {}),
  ...(params.adminOnly ? { adminOnly: true } : {}),
});

/**
 * 구서버 폴백 — `target`만 바꿔 `total`만 읽는다(limit=1).
 * 주문 축은 **전체 − 쿠폰 − 배송**으로 유도한다: 메모는 축이 정확히 하나라(2개면 서버가 400)
 * 세 축이 전체를 분할하고, 요청도 한 번 줄고, 칩 합계가 `총 N건`과 반드시 일치한다.
 */
const fetchCountsByProbe = async (
  base: GiftCsMemoLogParams,
): Promise<GiftCsMemoLogCounts> => {
  //* 축별 카운트는 축 필터와 무관해야 한다 — base의 target을 아래에서 명시적으로 덮어쓴다
  const probe = { ...base, page: 1, limit: 1 };
  const [all, coupon, delivery] = await Promise.all([
    getGiftCsMemoLog({ ...probe, target: 'all' }),
    getGiftCsMemoLog({ ...probe, target: 'coupon' }),
    getGiftCsMemoLog({ ...probe, target: 'delivery' }),
  ]);
  return {
    all: all.total,
    coupon: coupon.total,
    delivery: delivery.total,
    order: Math.max(0, all.total - coupon.total - delivery.total),
  };
};

/** `null` = 엔드포인트 미배포(404) → 칩에 숫자를 띄우지 않는다 */
export const useGiftCsMemoLogOverviewQuery = (
  params: GiftCsMemoLogParams,
  enabled = true,
) => {
  const base = overviewBaseParams(params);

  return useQuery<GiftCsMemoLogOverview | null>(
    ['gift-cs-memo', 'log-overview', JSON.stringify(base)],
    async () => {
      try {
        const summary = await getGiftCsMemoLogSummary(base);
        if (summary) {
          const { counts, ...distributions } = summary;
          return { counts, distributions };
        }
        return { counts: await fetchCountsByProbe(base), distributions: null };
      } catch (e) {
        if (isCsMemoNotFound(e)) return null;
        throw e;
      }
    },
    {
      enabled,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );
};

export interface GiftCsMemoLogAllResult {
  items: GiftCsMemoLogItem[];
  total: number;
  /** true = 상한(2000건)에 걸려 일부만 집계 — 화면이 "전체"라고 말하면 안 된다 */
  capped: boolean;
}

/**
 * 조회 조건 전체 수집 (통계 패널용). 페이지네이션을 넘겨가며 최대 2000건.
 * 무거우니 **패널을 열 때만** 돈다(enabled) — 표는 기존 페이지 조회 그대로 쓴다.
 */
export const useGiftCsMemoLogAllQuery = (
  params: GiftCsMemoLogParams,
  enabled: boolean,
) => {
  const base: GiftCsMemoLogParams = {
    ...(params.from ? { from: params.from } : {}),
    ...(params.to ? { to: params.to } : {}),
    ...(params.target && params.target !== 'all' ? { target: params.target } : {}),
    ...(params.q ? { q: params.q } : {}),
    ...(params.adminOnly ? { adminOnly: true } : {}),
  };

  return useQuery<GiftCsMemoLogAllResult | null>(
    ['gift-cs-memo', 'log-all', JSON.stringify(base)],
    async () => {
      try {
        return await getGiftCsMemoLogAll(base);
      } catch (e) {
        if (isCsMemoNotFound(e)) return null;
        throw e;
      }
    },
    {
      enabled,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );
};

export const useCreateGiftCsMemo = () => {
  const queryClient = useQueryClient();

  return useMutation(createGiftCsMemo, {
    onSuccess: (memo) => {
      // 목록 배지가 즉시 +1 되도록 이미 받아둔 캐시에 append
      const append = (target: GiftCsMemoTarget) => {
        queryClient.setQueryData<GiftCsMemo[] | null | undefined>(
          giftCsMemoQueryKey(target),
          (prev) => (Array.isArray(prev) ? [...prev, memo] : prev),
        );
      };
      if (memo.giftCoupon) append({ giftCoupon: memo.giftCoupon });
      if (memo.giftDelivery) append({ giftDelivery: memo.giftDelivery });
      // 쿠폰·배송 단위 메모도 주문 축 통합 타임라인에 함께 실린다
      if (memo.giftOrder) {
        append({ giftOrder: memo.giftOrder });
        void queryClient.invalidateQueries(
          giftCsMemoQueryKey({ giftOrder: memo.giftOrder }),
        );
      }

      // 목록 일괄 조회분(주문 여러 건)은 해당 주문만 즉시 갈아끼우고(배지 반영),
      // 서버 기준으로 한 번 더 맞춘다 — 저장 직전에 시작된 목록 조회가 이 메모를
      // 못 본 응답으로 캐시를 덮어쓰는 경우까지 자동 정정된다 (배치라 1요청).
      queryClient.setQueriesData<Record<string, GiftCsMemo[]> | null | undefined>(
        ['gift-cs-memo', 'orders'],
        (prev) => {
          const orderId = memo.giftOrder;
          if (!prev || !orderId) return prev;
          const existing = prev[orderId];
          if (!existing) return prev;
          return { ...prev, [orderId]: [...existing, memo] };
        },
      );
      void queryClient.invalidateQueries(['gift-cs-memo', 'orders']);
      // CS 응대 로그 목록도 최신으로 (조건별 페이지가 여러 개일 수 있어 통째 무효화)
      void queryClient.invalidateQueries(['gift-cs-memo', 'log']);
      void queryClient.invalidateQueries(['gift-cs-memo', 'log-all']);
      void queryClient.invalidateQueries(['gift-cs-memo', 'log-overview']);
    },
  });
};

/** 메모 삭제 — 캐시에서 즉시 제거 (백엔드 미배포면 호출부에서 안내) */
export const useDeleteGiftCsMemo = () => {
  const queryClient = useQueryClient();

  return useMutation(({ memo }: { memo: GiftCsMemo }) => deleteGiftCsMemo(memo._id), {
    onSuccess: (_data, { memo }) => {
      const drop = (target: GiftCsMemoTarget) => {
        queryClient.setQueryData<GiftCsMemo[] | null | undefined>(
          giftCsMemoQueryKey(target),
          (prev) => (Array.isArray(prev) ? prev.filter((m) => m._id !== memo._id) : prev),
        );
      };
      if (memo.giftCoupon) drop({ giftCoupon: memo.giftCoupon });
      if (memo.giftDelivery) drop({ giftDelivery: memo.giftDelivery });
      if (memo.giftOrder) drop({ giftOrder: memo.giftOrder });

      queryClient.setQueriesData<Record<string, GiftCsMemo[]> | null | undefined>(
        ['gift-cs-memo', 'orders'],
        (prev) => {
          const orderId = memo.giftOrder;
          if (!prev || !orderId) return prev;
          const existing = prev[orderId];
          if (!existing) return prev;
          return { ...prev, [orderId]: existing.filter((m) => m._id !== memo._id) };
        },
      );
      void queryClient.invalidateQueries(['gift-cs-memo', 'orders']);
      void queryClient.invalidateQueries(['gift-cs-memo', 'log']);
      void queryClient.invalidateQueries(['gift-cs-memo', 'log-all']);
      void queryClient.invalidateQueries(['gift-cs-memo', 'log-overview']);
      if (memo.giftOrder) {
        void queryClient.invalidateQueries(
          giftCsMemoQueryKey({ giftOrder: memo.giftOrder }),
        );
      }
    },
  });
};
